import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const DEFAULT_SHOTS = [
    {
        url: "https://images.unsplash.com/photo-1779911915389-914430fad5e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwbW9kZWwlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODgwNjQ0MDV8MA&ixlib=rb-4.1.0&q=85",
        caption: "Look 01 — After dark, off grid",
    },
    {
        url: "https://images.unsplash.com/photo-1779911915399-aeaaa1153847?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwbW9kZWwlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODgwNjQ0MDV8MA&ixlib=rb-4.1.0&q=85",
        caption: "Look 02 — Silver hour",
    },
];

const CLIPS = [
    "polygon(0 0, 100% 3%, 97% 100%, 0 97%)",
    "polygon(3% 0, 100% 0, 100% 97%, 0 100%)",
];

const ParallaxShot = ({ shot, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

    return (
        <motion.figure
            ref={ref}
            initial={{ y: 70, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: EASE, delay: index * 0.12 }}
            className={index === 1 ? "md:mt-40" : ""}
            data-testid={`lookbook-shot-${index + 1}`}
        >
            <div className="overflow-hidden border border-zinc-800" style={{ clipPath: CLIPS[index % CLIPS.length] }}>
                <motion.img
                    src={shot.url}
                    alt={shot.caption}
                    loading="lazy"
                    style={{ y, scale: 1.18 }}
                    className="w-full aspect-[3/4] object-cover grayscale contrast-125 brightness-90"
                />
            </div>
            <figcaption className="flex items-center justify-between mt-4 px-1">
                <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">{shot.caption}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-signal" />
            </figcaption>
        </motion.figure>
    );
};

export const Lookbook = ({ lede = "Shot where the bars drop to zero.", shots = DEFAULT_SHOTS }) => (
    <section id="lookbook" className="relative px-6 sm:px-12 py-32 sm:py-44 border-t border-zinc-900" data-testid="lookbook-section">
        <motion.h2
            initial={{ y: 60, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE }}
            className="font-display font-semibold uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl mb-6"
        >
            Lookbook
        </motion.h2>
        <motion.p
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: EASE, delay: 0.1 }}
            className="font-serif italic text-xl sm:text-2xl text-zinc-400 mb-20 sm:mb-28"
        >
            {lede}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16 max-w-6xl mx-auto">
            {shots.map((s, i) => (
                <ParallaxShot key={i} shot={s} index={i} />
            ))}
        </div>
    </section>
);
